import {PageHeader} from '../ui/PageHeader';
import {formatDateTime} from '../dates';
import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { alphaApi as api } from './alpha-request';
import { ErrorState, Loading } from '../ui/State';
const time = formatDateTime;
export function ResidentRequestAlpha() {
  const { id } = useParams(),
    root = `/api/portal/requests/${id}`,
    [data, setData] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState('');
  const load = () => api(root).then(setData);
  useEffect(() => {
    void load().catch(setError);
  }, [id]);
  async function save(url: string, body: object) {
    setBusy(true);
    setError(undefined);
    try {
      await api(root + url, {
        method: 'POST',
        body: JSON.stringify({ ...body, idempotencyKey: crypto.randomUUID() }),
      });
      setNotice('Your response is saved.');
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  function preferences(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void save('/preferences', { text: new FormData(e.currentTarget).get('text') });
  }
  return (
    <>
      <PageHeader title={data?.title ?? 'Your request'} {...(data?{status:{text:data.status}}:{})}/>
      {Boolean(error) && <ErrorState error={error} />} {notice && <p role="status">{notice}</p>}
      {!data && !error ? (
        <Loading />
      ) : !data ? null : (
        <>
          <section className="panel">
            <h2>Access preferences</h2>
            <form className="form" onSubmit={preferences}>
              <label>
                Suitable times and access needs
                <textarea name="text" required minLength={3} maxLength={1000} />
              </label>
              <p>Do not include keysafe codes or other security credentials.</p>
              <button disabled={busy}>Save preferences</button>
            </form>
          </section>
          <section className="panel">
            <h2>Appointments</h2>
            {!data.offers.length && <p>No appointment has been offered yet.</p>}
            {data.offers.map((o: any) => (
              <article key={o.id}>
                <h3>
                  {time(o.starts_at)} – {time(o.ends_at)}
                </h3>
                <p>
                  {o.status}
                  {!o.current ? ' · This appointment has changed or ended.' : ''}
                </p>
                {o.current && ['Offered', 'Confirmed'].includes(o.status) && (
                  <>
                    <button
                      disabled={busy || o.status === 'Confirmed'}
                      onClick={() =>
                        void save(`/appointments/${o.id}/respond`, {
                          decision: 'Confirm',
                          expectedVersion: o.version,
                        })
                      }
                    >
                      Confirm appointment
                    </button>
                    <form
                      className="form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void save(`/appointments/${o.id}/respond`, {
                          decision: 'Reschedule',
                          reason: new FormData(e.currentTarget).get('reason'),
                          expectedVersion: o.version,
                        });
                      }}
                    >
                      <label>
                        Alternative availability
                        <textarea name="reason" required minLength={3} maxLength={1000} />
                      </label>
                      <button disabled={busy}>Request a different time</button>
                      <p>
                        The existing visit remains booked until your manager arranges a replacement.
                      </p>
                    </form>
                  </>
                )}
              </article>
            ))}
          </section>
          <section className="panel">
            <h2>Updates</h2>
            {!data.events.length&&<p>No published updates were returned.</p>}
            {data.events.map((e: any, i: number) => (
              <p key={i}>
                <strong>
                  {time(e.created_at)} · {e.event_type}
                </strong>
                <br />
                {e.text}
              </p>
            ))}
          </section>
        </>
      )}
    </>
  );
}
export function ResidentAppointmentsStaffAlpha() {
  const [q] = useSearchParams(),
    building = q.get('building'),
    [data, setData] = useState<any>(),
    [selected, setSelected] = useState(''),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState('');
  const load = () => api(`/api/alpha/resident-appointments?building=${building}`).then(setData);
  useEffect(() => {
    if (building) void load().catch(setError);
  }, [building]);
  async function save(path: string, body: object) {
    setBusy(true);
    try {
      await api(`/api/alpha/resident-appointments/${selected}/${path}`, {
        method: 'POST',
        body: JSON.stringify({ ...body, idempotencyKey: crypto.randomUUID() }),
      });
      setNotice('Saved.');
      await load();
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  const item = data?.cases.find((c: any) => c.id === selected);
  return (
    <>
      <h1>Resident appointments and updates</h1>
      {Boolean(error) && <ErrorState error={error} />} {notice && <p role="status">{notice}</p>}
      {!building ? (
        <p>Open this page in a building.</p>
      ) : !data && !error ? (
        <Loading />
      ) : !data ? null : (
        <>
          <label>
            Repair request
            <select value={selected} onChange={(e) => setSelected(e.target.value)}>
              <option value="">Choose a request</option>
              {data.cases.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </label>
          {!data.cases.length&&<p className="empty">No resident repair requests are available in this building. Appointments can be offered after a request has a booked visit.</p>}
          {item && (
            <>
              <section className="panel">
                <h2>Offer an appointment</h2>
                <p>Book the visit through the existing Work and Calendar journey first.</p>
                <form
                  className="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void save('offer', { visitId: new FormData(e.currentTarget).get('visit') });
                  }}
                >
                  <label>
                    Booked visit
                    <select name="visit" required>
                      <option value="">Choose a visit</option>
                      {data.visits
                        .filter((v: any) => !v.premises_id || v.premises_id === item.premises_id)
                        .map((v: any) => (
                          <option key={v.id} value={v.id}>
                            {v.title} · {time(v.starts_at)}
                          </option>
                        ))}
                    </select>
                  </label>
                  <button disabled={busy}>Offer appointment</button>
                </form>
              </section>
              <section className="panel">
                <h2>Publish an update</h2>
                <form
                  className="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void save('update', { text: new FormData(e.currentTarget).get('text') });
                  }}
                >
                  <label>
                    Message for this resident
                    <textarea name="text" required minLength={5} maxLength={2000} />
                  </label>
                  <button disabled={busy}>Publish update</button>
                </form>
              </section>
              <h2>Offered appointments</h2>
              {data.offers
                ?.filter((o: any) => o.case_id === selected)
                .map((o: any) => (
                  <p key={o.id}>
                    {time(o.starts_at)} · {o.status}{' '}
                    {o.status !== 'Withdrawn' && (
                      <button
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            await api('/api/alpha/resident-appointments/' + o.id + '/withdraw', {
                              method: 'POST',
                              body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
                            });
                            await load();
                            setError(undefined);
                          } catch (e) {
                            setError(e);
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        Withdraw offer
                      </button>
                    )}
                  </p>
                ))}
              <h2>Resident responses</h2>
              {data.events
                .filter((e: any) => e.case_id === selected)
                .map((e: any, i: number) => (
                  <p key={i}>
                    {e.event_type}: {e.detail.text}
                  </p>
                ))}
            </>
          )}
        </>
      )}
    </>
  );
}
